<template>
  <nav aria-label="Progress">
    <ol role="list" class="flex items-center">
      <li
        v-for="(step, index) in computedSteps"
        :key="index"
        :class="[
          index !== computedSteps.length - 1 ? 'pr-8 sm:pr-20' : '',
          'relative'
        ]"
      >
        <template v-if="step.status === 'complete'">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="h-0.5 w-full bg-secondary" />
          </div>
          <div
            :class="[
              'relative flex items-center justify-center rounded-full bg-secondary',
              size
            ]"
          >
            <Icon
              name="check"
              :class="[iconSize, 'text-white']"
              aria-hidden="true"
              type="filled"
            />
            <span class="sr-only">{{ step.name }}</span>
          </div>
        </template>

        <template v-else-if="step.status === 'current'">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="h-0.5 w-full bg-gray-300" />
          </div>
          <div
            :class="[
              'relative flex items-center justify-center rounded-full border-2 border-secondary bg-white',
              size
            ]"
          >
            <div :class="['rounded-full bg-secondary', innerCircleSize]" />
            <span class="sr-only">{{ step.name }}</span>
          </div>
        </template>

        <!-- Upcoming Step -->
        <template v-else>
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="h-0.5 w-full bg-gray-300" />
          </div>
          <div
            :class="[
              'relative flex items-center justify-center rounded-full border-2 border-gray-300 bg-white',
              size
            ]"
          >
            <span class="sr-only">{{ step.name }}</span>
          </div>
        </template>
      </li>
    </ol>
  </nav>
</template>

<script setup>
  import { computed } from 'vue';
  import Icon from '@/components/global/Icon.vue';

  const props = defineProps({
    totalSteps: {
      type: Number,
      required: true,
      validator: (value) => value > 0
    },
    currentStep: {
      type: Number,
      required: true,
      validator: (value) => value >= 0
    },
    size: {
      type: String,
      default: 'size-8',
      validator: (value) =>
        ['size-6', 'size-8', 'size-10', 'size-12'].includes(value)
    }
  });

  const innerCircleSize = computed(() => {
    const sizeMap = {
      'size-6': 'size-2',
      'size-8': 'size-3',
      'size-10': 'size-4',
      'size-12': 'size-5'
    };
    return sizeMap[props.size] || 'size-3';
  });

  const iconSize = computed(() => {
    const sizeMap = {
      'size-6': 'text-sm',
      'size-8': 'text-base',
      'size-10': 'text-lg',
      'size-12': 'text-xl'
    };
    return sizeMap[props.size] || 'text-base';
  });

  const computedSteps = computed(() => {
    const steps = [];
    for (let i = 0; i < props.totalSteps; i++) {
      let status = 'upcoming';

      if (i < props.currentStep - 1) {
        status = 'complete';
      } else if (i === props.currentStep - 1) {
        status = 'current';
      }

      steps.push({
        name: `Step ${i + 1}`,
        status
      });
    }
    return steps;
  });
</script>
